import axios from "axios";
import { API_URL } from "../constants";
import {
  InvoiceExpectationsResponse,
  InvoiceMailbox,
  InvoiceMailboxPayload,
  InvoiceMailboxRefreshResult,
  InvoicePermissionHolders,
  InvoiceSenderRule,
  InvoiceWaitingDoc,
  MailWithoutDocumentsResponse,
  PurchaseDocCounts,
  PurchaseDocDetail,
  PurchaseDocFile,
  PurchaseDocFilters,
  PurchaseDocListItem,
  PurchaseDocSignRequestPayload,
  PurchaseDocSignRequestResult,
  PurchaseDocTab,
  PurchasePeriod,
  PurchaseListPagination,
  PurchaseSupplierArrangement,
  PurchaseSupplierPayload,
  PurchaseSupplierSettings,
  PurchaseVatLine,
} from "../types/InvoiceInbox";

/**
 * Invoice Inbox API. Bare axios, like every other service here — the global
 * interceptor already attaches credentials.
 */

export async function fetchPurchaseDocs(
  companyId: string,
  params: { page?: number; tab?: PurchaseDocTab; filtersValue?: PurchaseDocFilters }
): Promise<{ docs: PurchaseDocListItem[]; pagination: PurchaseListPagination }> {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/fetch`, params);
  return res.data;
}

export async function fetchPurchaseDoc(companyId: string, docId: string): Promise<PurchaseDocDetail> {
  const res = await axios.get(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}`);
  return res.data;
}

/** Badge + dashboard widget: the slots waiting on the current user. */
export async function fetchWaitingForMe(
  companyId: string
): Promise<{ count: number; docs: InvoiceWaitingDoc[] }> {
  const res = await axios.get(`${API_URL}/${companyId}/invoice-inbox/docs/waiting-for-me`);
  return res.data;
}

/** Who may act on each queue — the strip above the register's tabs. */
export async function fetchInvoicePermissionHolders(companyId: string): Promise<InvoicePermissionHolders> {
  const res = await axios.get(`${API_URL}/${companyId}/invoice-inbox/permission-holders`);
  return res.data;
}

/** The accountant's "typed into the accounting program" mark. */
export async function setPurchaseDocBookkept(
  companyId: string,
  docId: string,
  entered: boolean
): Promise<{ doc: PurchaseDocListItem }> {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/bookkept`, { entered });
  return res.data;
}

/** The "Apmokėta" tick — a person confirming this document is settled,
 * independent of `paymentSource` (which says who paid, not whether it is
 * done). Same shape as `setPurchaseDocBookkept`. */
export async function setPurchaseDocMarkedPaid(
  companyId: string,
  docId: string,
  paid: boolean
): Promise<{ doc: PurchaseDocListItem }> {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/paid`, { paid });
  return res.data;
}

/**
 * The month-handover screen: one row per accounting period, newest first, the
 * `null` period (accounting period could not be determined) last. Scoped by
 * tab + filters — every count in a header is the same query the rows under it
 * would run, not a company-wide number (erp-backend periods/fetch.js).
 */
export async function fetchPurchasePeriods(
  companyId: string,
  params: { tab: PurchaseDocTab; filtersValue: PurchaseDocFilters }
): Promise<{ tab: PurchaseDocTab; periods: PurchasePeriod[] }> {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/periods`, params);
  return res.data;
}

/**
 * The register's tab badges. One count per tab, each computed through the
 * same query builder its own rows use — so a badge and the list behind it can
 * never disagree. `status`/`period` in `filtersValue` are stripped
 * server-side (a stray status filter must never collapse every badge to one
 * number); pass the flag/search filters as-is.
 */
export async function fetchPurchaseDocCounts(
  companyId: string,
  filtersValue: PurchaseDocFilters
): Promise<{ counts: PurchaseDocCounts }> {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/counts`, { filtersValue });
  return res.data;
}

export async function addPurchaseDocComment(
  companyId: string,
  docId: string,
  params: { body: string; mentions?: string[] }
) {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/comments`, params);
  return res.data;
}

/**
 * Send for approval. `blockers` comes back as a LIST on failure — "you cannot
 * send this" is useless, "the supplier is unresolved and the bank account is
 * unverified" is a to-do list.
 */
export async function dispatchPurchaseDoc(
  companyId: string,
  docId: string,
  params?: { managerUserId?: string | null; accountantUserId?: string | null }
) {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/dispatch`, params || {});
  return res.data;
}

export async function approvePurchaseDoc(companyId: string, docId: string, comment?: string) {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/approve`, { comment });
  return res.data;
}

/** A reason is mandatory server-side: a rejection without one is unactionable. */
export async function rejectPurchaseDoc(companyId: string, docId: string, reason: string) {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/reject`, { reason });
  return res.data;
}

export async function recallPurchaseDoc(companyId: string, docId: string) {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/recall`, {});
  return res.data;
}

/**
 * How this invoice was actually paid — the company account, or an employee's
 * own pocket (money the company owes that person back). Ordinary register
 * work: unlike the supplier-level arrangement below, this is stated per
 * document and never touches approval routing, so no extra permission check
 * applies beyond reaching the module. `source: null` clears the mark back to
 * "nobody has said yet" — a real state to return to, not just a starting one.
 */
export async function setPurchaseDocPaymentSource(
  companyId: string,
  docId: string,
  params: { source: "company" | "employee" | null; employeeId?: string }
) {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/payment`, params);
  return res.data;
}

/**
 * Clear a held bank account. The note is what the control is actually made of:
 * who was called, on which number from the supplier master.
 */
/**
 * "Read it again" — only accepted for a document the model never managed to
 * read, so the reading a person is responsible for can never be overwritten.
 */
export async function retryExtraction(companyId: string, docId: string) {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/reextract`);
  return res.data;
}

export async function verifyBankAccount(
  companyId: string,
  accountId: string,
  params: { note: string; method?: string; decision?: "confirm" | "reject"; docId?: string }
) {
  const res = await axios.post(
    `${API_URL}/${companyId}/invoice-inbox/bank-accounts/${accountId}/verify`,
    params
  );
  return res.data;
}

/**
 * Create a new supplier customer from the document's fields, or link the
 * document to one that already exists — the same decision, two shapes of
 * payload. A create that collides with an existing VAT/company code comes
 * back 409 CUSTOMER_EXISTS with that customer attached, so the caller can
 * re-submit in link mode instead of typing a duplicate.
 */
export async function linkOrCreateSupplier(
  companyId: string,
  docId: string,
  payload: PurchaseSupplierPayload
) {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/supplier`, payload);
  return res.data;
}

/**
 * Take a document's supplier back — the way out of a wrong link. Only before
 * the document is sent for approval; the server also stops linking this
 * invoice's name to that card automatically. The document is left unlinked,
 * for the create-or-link form.
 */
export async function unlinkSupplier(companyId: string, docId: string) {
  const res = await axios.delete(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/supplier`);
  return res.data;
}

/**
 * The supplier-level arrangements: this counterparty is paid first and
 * invoices afterwards, so FUTURE documents from them skip the manager's
 * approval step; and/or it invoices every month, so the register says when a
 * month's invoice has not arrived. Send only the one being changed — the
 * server leaves the other as it is. Requires the settings right — the
 * endpoint checks it again, this is not a policy the client can rely on
 * enforcing.
 */
export async function saveSupplierSettings(
  companyId: string,
  supplierId: string,
  params: PurchaseSupplierSettings
) {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/suppliers/${supplierId}/settings`, params);
  return res.data;
}

/**
 * Every supplier with "invoices arrive already paid" and/or "invoices every
 * month", by name. `truncated` means there are more than the 500 returned.
 */
export async function fetchSupplierArrangements(
  companyId: string
): Promise<{ suppliers: PurchaseSupplierArrangement[]; truncated: boolean }> {
  const res = await axios.get(`${API_URL}/${companyId}/invoice-inbox/suppliers/rules`);
  return res.data;
}

/** How the register is run. `strictApproval` is a server switch, not a company setting. */
export async function fetchInvoiceInboxConfig(companyId: string): Promise<{ strictApproval: boolean }> {
  const res = await axios.get(`${API_URL}/${companyId}/invoice-inbox/config`);
  return res.data;
}

/** Monthly invoices that are due and have not arrived, newest period first. */
export async function fetchInvoiceExpectations(companyId: string): Promise<InvoiceExpectationsResponse> {
  const res = await axios.get(`${API_URL}/${companyId}/invoice-inbox/expectations`);
  return res.data;
}

/** Recent mail that produced no document, each with its reason. */
export async function fetchMailWithoutDocuments(companyId: string): Promise<MailWithoutDocumentsResponse> {
  const res = await axios.get(`${API_URL}/${companyId}/invoice-inbox/mail-without-documents`);
  return res.data;
}

/**
 * "Not expected this month": dismisses one supplier's gap for one period only
 * — the next month is judged again. `waive: false` takes the dismissal back.
 */
export async function waiveInvoiceExpectation(
  companyId: string,
  params: { supplierId: string; period: string; note?: string; waive?: false }
) {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/expectations/waive`, params);
  return res.data;
}

/**
 * Plain URL for <iframe>/<img>: the document's PRIMARY file, streamed by the
 * register itself with its real content type. Unlike the files endpoint it
 * works before the OneDrive copy exists (`doc.file` still null) — the intake's
 * local copy is served then. Append `?download=1` for an attachment
 * disposition instead of inline.
 */
export function purchaseDocContentUrl(companyId: string, docId: string): string {
  return `${API_URL}/${companyId}/invoice-inbox/docs/${docId}/content`;
}

/** Same as `purchaseDocContentUrl`, for one of the document's additional files. */
export function purchaseDocFileContentUrl(companyId: string, docId: string, fileId: string): string {
  return `${API_URL}/${companyId}/invoice-inbox/docs/${docId}/files/${fileId}/content`;
}

/**
 * One more file into an existing document (the rest of a split invoice, the
 * work act). Refusals come back as `code`: DUPLICATE_FILE (the same bytes are
 * already in this document), DOC_DELETED, ATTACHMENT_REFUSED.
 */
export async function addPurchaseDocFile(
  companyId: string,
  docId: string,
  file: File
): Promise<{ ok: boolean; file: PurchaseDocFile }> {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/files`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

/** Takes an additional file out again. A signed file is refused (FILE_IS_SIGNED). */
export async function removePurchaseDocFile(
  companyId: string,
  docId: string,
  fileId: string
): Promise<{ ok: boolean }> {
  const res = await axios.delete(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/files/${fileId}`);
  return res.data;
}

/**
 * Folds THIS document into `targetDocId`: its files become the target's
 * additional files and this row is soft-deleted. Only for a document nobody
 * has sent for approval yet (new / needs_review / rejected) — otherwise
 * SOURCE_NOT_MERGEABLE. `target` is the target document's id, not the row
 * itself — the caller already has the row it picked (see
 * invoice-doc-merge-dialog.tsx).
 */
export async function mergePurchaseDocInto(
  companyId: string,
  docId: string,
  targetDocId: string
): Promise<{ ok: boolean; target: string; moved: number }> {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/merge-into`, {
    targetDocId,
  });
  return res.data;
}

/**
 * Asks for e-signatures on one file of the document: the primary file
 * (`fileKey: 'primary'`) or one of its additional files. Dokobit takes
 * employees and the supplier's contacts; Click (signing inside the ERP) takes
 * employees only. Refusals come back as `code` (NO_SIGNERS, NOT_A_PDF,
 * ALREADY_SIGNED, SIGNING_IN_PROGRESS, DOKOBIT_NOT_CONFIGURED, …). A pending
 * request is cancelled through the generic `cancelFileSigning`.
 */
export async function requestPurchaseDocSignature(
  companyId: string,
  docId: string,
  payload: PurchaseDocSignRequestPayload
): Promise<PurchaseDocSignRequestResult> {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/sign-request`, payload);
  return res.data;
}

/** A route param cannot be null — 'unassigned' is the sentinel the periods and export endpoints agree on. */
const NULL_PERIOD_SEGMENT = "unassigned";

/** What downloadPurchasePeriodExport throws on a refusal — `code` drives the message shown. */
export type PeriodExportError = Error & {
  code?: string;
  count?: number;
  limit?: number;
  estimatedBytes?: number;
};

export function fileNameFromContentDisposition(header: string | undefined, fallback: string): string {
  if (!header) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  if (plain?.[1]) {
    try {
      return decodeURIComponent(plain[1].trim());
    } catch {
      return plain[1].trim();
    }
  }
  return fallback;
}

/**
 * `responseType: 'blob'` means a JSON refusal body (400 TOO_MANY_DOCUMENTS /
 * EXPORT_TOO_LARGE / INVALID_PERIOD) also arrives as a Blob, not parsed JSON —
 * axios does not know the body is text until something reads it. Read it back
 * out so the caller can map `code` to an explanation instead of a bare failure.
 */
async function readBlobErrorPayload(data: unknown): Promise<any | null> {
  if (!(data instanceof Blob)) return null;
  try {
    const text = await data.text();
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Saves a downloaded blob under the given name without leaving the page. */
export function saveBlob(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

/** Turns a blob-typed refusal back into an error whose `code` the caller can explain. */
async function toPeriodExportError(err: any): Promise<any> {
  const payload = await readBlobErrorPayload(err?.response?.data);
  if (!payload) return err;
  const enriched: PeriodExportError = new Error(payload.message || payload.code || "EXPORT_FAILED");
  enriched.code = payload.code;
  enriched.count = payload.count;
  enriched.limit = payload.limit;
  enriched.estimatedBytes = payload.estimatedBytes;
  return enriched;
}

/**
 * The month handover itself: a ZIP of the period's files plus summary.csv,
 * fetched through the app's own axios instance (so the session cookie goes
 * along) and saved without leaving the page. It follows the tab + filters
 * being browsed — the "Approved" tab hands over the approved invoices only —
 * exactly like the PDF list below. The filename comes from the server's
 * Content-Disposition, not guessed client-side; the fallback mirrors what the
 * server names it (tab 'all' drops the `-<tab>` suffix).
 */
export async function downloadPurchasePeriodExport(
  companyId: string,
  period: string | null,
  params: { tab: PurchaseDocTab; filtersValue: PurchaseDocFilters }
): Promise<void> {
  const segment = period ? encodeURIComponent(period) : NULL_PERIOD_SEGMENT;
  const periodSlug = period || NULL_PERIOD_SEGMENT;
  const fallbackName = `invoice-inbox-${periodSlug}${params.tab === "all" ? "" : `-${params.tab}`}.zip`;
  try {
    const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/periods/${segment}/export`, params, {
      responseType: "blob",
    });
    saveBlob(res.data, fileNameFromContentDisposition(res.headers["content-disposition"], fallbackName));
  } catch (err: any) {
    throw await toPeriodExportError(err);
  }
}

/**
 * The month as a printable PDF list (supplier, number, dates, amounts, status,
 * totals per currency). It prints what the month shows: the same tab and
 * filters go along, so the "Approved" tab prints the approved invoices only.
 */
export async function downloadPurchasePeriodRegisterPdf(
  companyId: string,
  period: string | null,
  params: { tab: PurchaseDocTab; filtersValue: PurchaseDocFilters; lang: string }
): Promise<void> {
  const segment = period ? encodeURIComponent(period) : NULL_PERIOD_SEGMENT;
  try {
    const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/periods/${segment}/register-pdf`, params, {
      responseType: "blob",
    });
    saveBlob(
      res.data,
      fileNameFromContentDisposition(
        res.headers["content-disposition"],
        `invoice-register-${period || NULL_PERIOD_SEGMENT}.pdf`
      )
    );
  } catch (err: any) {
    throw await toPeriodExportError(err);
  }
}

/**
 * "Remove" is a soft delete with a mandatory reason — these are ten-year
 * records, and the person clicking is clearing a queue rather than making a
 * legal decision.
 */
export async function deletePurchaseDoc(companyId: string, docId: string, reason: string) {
  const res = await axios.delete(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}`, {
    data: { reason },
  });
  return res.data;
}

export async function restorePurchaseDoc(companyId: string, docId: string) {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}/restore`, {});
  return res.data;
}

/** Manual entry and correction. The server allow-lists which columns may move. */
export async function patchPurchaseDoc(
  companyId: string,
  docId: string,
  changes: Record<string, string | number | null>
) {
  const res = await axios.patch(`${API_URL}/${companyId}/invoice-inbox/docs/${docId}`, changes);
  return res.data;
}

/**
 * Override the VAT classifier (PVM1…PVM100) of one VAT line; null hands it
 * back to the backend's automatic rules. Answers with all the document's VAT
 * lines as they now are.
 */
export async function setPurchaseVatClassifier(
  companyId: string,
  docId: string,
  lineId: string,
  pvmClassifier: string | null
): Promise<{ ok: boolean; vatLines: PurchaseVatLine[] }> {
  const res = await axios.put(
    `${API_URL}/${companyId}/invoice-inbox/docs/${docId}/vat-lines/${lineId}/classifier`,
    { pvmClassifier }
  );
  return res.data;
}

/**
 * Paper, a photo, an invoice sent to somebody's personal address. Not a
 * convenience: with a brand-new mailbox most invoices arrive this way at first.
 */
export async function uploadPurchaseDoc(companyId: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/upload`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function fetchSenderRules(companyId: string): Promise<{ rules: InvoiceSenderRule[] }> {
  const res = await axios.get(`${API_URL}/${companyId}/invoice-inbox/senders`);
  return res.data;
}

/**
 * Takes a sender rule away. It decides about future mail only: what the rule
 * already held stays listed. A second call for the same rule answers 404.
 */
export async function unblockSender(companyId: string, ruleId: string): Promise<{ ok: true; id: string }> {
  const res = await axios.delete(`${API_URL}/${companyId}/invoice-inbox/senders/${ruleId}`);
  return res.data;
}

/**
 * Blocking stops FUTURE mail from becoming documents. It deletes nothing, and
 * the response says how many already-accepted invoices came from this sender —
 * a domain rule aimed at a newsletter easily covers the invoice address too.
 */
export async function blockSender(
  companyId: string,
  params: { pattern: string; scope?: "address" | "domain"; action?: "block" | "quarantine" | "trust"; reason?: string }
) {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/senders`, params);
  return res.data;
}

/**
 * Mailbox settings. Every call needs 'Manage invoice inbox settings' — reaching
 * the register is not enough to see the addresses invoices arrive at.
 */
export async function fetchInvoiceMailboxes(companyId: string): Promise<{ mailboxes: InvoiceMailbox[] }> {
  const res = await axios.get(`${API_URL}/${companyId}/invoice-inbox/mailboxes`);
  return res.data;
}

export async function saveInvoiceMailbox(
  companyId: string,
  payload: InvoiceMailboxPayload
): Promise<{ mailboxId: string }> {
  const res = await axios.put(`${API_URL}/${companyId}/invoice-inbox/mailboxes`, payload);
  return res.data;
}

/** A real IMAP login before saving — the alternative is an empty register. */
export async function testInvoiceMailbox(
  companyId: string,
  payload: InvoiceMailboxPayload
): Promise<{ ok: boolean; labelFound: boolean | null; folderFound: boolean | null; folders?: string[]; error?: string }> {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/mailboxes/test`, payload);
  return res.data;
}

/** "Check mail now" — the sweep runs in the worker, this waits for its answer. */
export async function refreshInvoiceMailboxes(
  companyId: string,
  mailboxId?: string
): Promise<{ mailboxes: InvoiceMailboxRefreshResult[] }> {
  const res = await axios.post(`${API_URL}/${companyId}/invoice-inbox/mailboxes/refresh`, { mailboxId });
  return res.data;
}

/**
 * `disable` keeps the row so everything it collected keeps its origin;
 * `delete` is only accepted for a disconnected mailbox that never received mail.
 */
export async function deleteInvoiceMailbox(
  companyId: string,
  mailboxId: string,
  mode: "disable" | "delete"
) {
  const res = await axios.delete(
    `${API_URL}/${companyId}/invoice-inbox/mailboxes/${mailboxId}?mode=${mode}`
  );
  return res.data;
}
